import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Globe, Building2, Loader2, Check } from "lucide-react";
import { cn } from "../../lib/utils";
import type { PortalShellScopeSelectorProps, PortalShellScopeOption } from "../../contracts/portal-shell";

export function PortalShellScopeSelector({
  triggerLabel,
  triggerBadgeLabel,
  title = "Switch Scope",
  loading = false,
  emptyMessage = "No scopes available.",
  options,
  fullWidth = false,
  onSelect,
  className,
}: PortalShellScopeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderOption = (option: PortalShellScopeOption) => {
    const Icon = option.kind === "global" ? Globe : Building2;
    
    return (
      <button
        key={option.id}
        onClick={() => {
          if (!option.disabled && !option.loading) {
            onSelect(option.id);
            setIsOpen(false);
          }
        }}
        disabled={option.disabled || option.loading}
        className={cn(
          "flex w-full items-start gap-3 rounded-control px-3 py-2.5 text-sm transition-colors text-left",
          "hover:bg-accent hover:text-accent-foreground",
          option.active && "bg-accent/50 font-medium",
          (option.disabled || option.loading) && "opacity-50 cursor-not-allowed"
        )}
      >
        <div className={cn(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground",
          option.active && "bg-primary/10 text-primary"
        )}>
          {option.loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Icon className="h-3 w-3" />
          )}
        </div>
        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate">{option.label}</span>
            {option.active && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
          </div>
          {option.description && (
            <span className="truncate text-[10px] text-muted-foreground">
              {option.description}
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div 
      ref={containerRef}
      className={cn("relative", fullWidth ? "w-full" : "w-auto", className)}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 rounded-control border border-border bg-card px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-ring",
          fullWidth && "w-full justify-between"
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{triggerLabel}</span>
          {triggerBadgeLabel && (
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
              {triggerBadgeLabel}
            </span>
          )}
        </div>
        <ChevronDown className={cn(
          "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </button>

      {isOpen && (
        <div className={cn(
          "absolute left-0 top-full z-50 mt-1 flex flex-col overflow-hidden rounded-panel border border-border bg-card shadow-lg animate-in fade-in zoom-in-95 duration-100",
          fullWidth ? "w-full" : "w-72"
        )}>
          {title && (
            <div className="border-b border-border bg-muted/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {title}
            </div>
          )}
          
          <div className="max-h-[300px] overflow-y-auto p-1.5">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mb-2 h-6 w-6 animate-spin" />
                <span className="text-xs">Loading scopes...</span>
              </div>
            ) : options.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              options.map(renderOption)
            )}
          </div>
        </div>
      )}
    </div>
  );
}
